import { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Briefcase, Users as UsersIcon, Plus, Save, Edit, Power, MessageSquare, Trash2, GripVertical, Car, Mail, Key } from 'lucide-react';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { ApiKeysPanel } from '../components/settings/ApiKeysPanel';

export const Settings = () => {
  return (
    <Layout title="Settings" subtitle="Service Catalogue, User Management & System Configuration">
      <div className="space-y-6 animate-fade-in" data-testid="settings-page">
        <Tabs defaultValue="services">
          <TabsList>
            <TabsTrigger value="services" className="gap-2" data-testid="tab-services">
              <Briefcase className="w-4 h-4" /> Service Catalogue
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
              <UsersIcon className="w-4 h-4" /> User Management
            </TabsTrigger>
            <TabsTrigger value="note-templates" className="gap-2" data-testid="tab-note-templates">
              <MessageSquare className="w-4 h-4" /> Note Templates
            </TabsTrigger>
            <TabsTrigger value="vehicle-catalogue" className="gap-2" data-testid="tab-vehicle-catalogue">
              <Car className="w-4 h-4" /> Vehicle Catalogue
            </TabsTrigger>
            <TabsTrigger value="api-keys" className="gap-2" data-testid="tab-api-keys">
              <Key className="w-4 h-4" /> API Keys
            </TabsTrigger>
          </TabsList>

          <TabsContent value="services" className="mt-4">
            <ServicesPanel />
          </TabsContent>
          <TabsContent value="users" className="mt-4">
            <UsersPanel />
          </TabsContent>
          <TabsContent value="note-templates" className="mt-4">
            <NoteTemplatesPanel />
          </TabsContent>
          <TabsContent value="vehicle-catalogue" className="mt-4">
            <VehicleCataloguePanel />
          </TabsContent>
          <TabsContent value="api-keys" className="mt-4">
            <ApiKeysPanel />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

// ============= SERVICES PANEL =============
const EMPTY_SERVICE = { code: '', name: '', billing_type: 'per_hour', default_rate: 0, minimum_units: 0, response_sla_minutes: 0 };

const ServicesPanel = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_SERVICE);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/services', { params: { include_inactive: true } });
      setServices(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_SERVICE); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      code: s.code, name: s.name, billing_type: s.billing_type,
      default_rate: s.default_rate, minimum_units: s.minimum_units,
      response_sla_minutes: s.response_sla_minutes || 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || (!editing && !form.code)) {
      toast.error('Name and code are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { code, ...payload } = form; // code is immutable post-create
        await api.patch(`/services/${editing.id}`, payload);
        toast.success('Service updated');
      } else {
        await api.post('/services', form);
        toast.success('Service created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/services/${s.id}`, { active: !s.active });
      toast.success(s.active ? `${s.name} deactivated` : `${s.name} reactivated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="bg-white border-slate-200" data-testid="services-panel">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading text-lg text-[#0D5B69]">Service Catalogue</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Default rates apply unless overridden per customer or contractor.</p>
        </div>
        <Button
          className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold"
          onClick={openCreate}
          data-testid="add-service-btn"
        >
          <Plus className="w-4 h-4" /> Add Service
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Code</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Billing</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Default Rate</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Min Units</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">SLA (min)</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>
            ) : services.map((s, idx) => (
              <TableRow key={s.id} className="hover:bg-slate-50/80" data-testid={`service-row-${idx}`}>
                <TableCell className="font-mono text-xs text-slate-600">{s.code}</TableCell>
                <TableCell className="font-medium text-sm text-slate-900">{s.name}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize text-xs">
                    {s.billing_type.replace('_', ' ')}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-sm text-slate-700">R {s.default_rate.toFixed(2)}</TableCell>
                <TableCell className="text-sm text-slate-600">{s.minimum_units}</TableCell>
                <TableCell className="text-sm">
                  {s.response_sla_minutes ? (
                    <Badge className="bg-[#80C030]/10 text-[#5a8c20] border-[#80C030]/30 font-mono">{s.response_sla_minutes}m</Badge>
                  ) : (
                    <span className="text-xs text-slate-400 italic">none</span>
                  )}
                </TableCell>
                <TableCell>
                  {s.active ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">Active</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(s)} data-testid={`edit-service-${idx}`}>
                      <Edit className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleActive(s)} data-testid={`toggle-service-${idx}`}>
                      <Power className={`w-4 h-4 ${s.active ? 'text-amber-600' : 'text-green-600'}`} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0D5B69]">
              {editing ? 'Edit Service' : 'New Service'}
            </DialogTitle>
            <DialogDescription>Default settings — can be overridden per customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Code *</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={!!editing} placeholder="e.g. HIJACK" />
              {editing && <p className="text-[11px] text-slate-400">Code cannot be changed once created</p>}
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Billing Type</Label>
              <Select value={form.billing_type} onValueChange={(v) => setForm({ ...form, billing_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_hour">Per Hour</SelectItem>
                  <SelectItem value="pro_rata">Pro Rata</SelectItem>
                  <SelectItem value="fixed">Fixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Default Rate (ex VAT)</Label>
                <Input type="number" min={0} step="0.01" value={form.default_rate}
                  onChange={(e) => setForm({ ...form, default_rate: Number(e.target.value || 0) })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Min Units</Label>
                <Input type="number" min={0} step="0.25" value={form.minimum_units}
                  onChange={(e) => setForm({ ...form, minimum_units: Number(e.target.value || 0) })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Response SLA (minutes)</Label>
              <Input
                type="number"
                min={0}
                step={1}
                value={form.response_sla_minutes}
                onChange={(e) => setForm({ ...form, response_sla_minutes: Math.max(0, Number(e.target.value || 0)) })}
                data-testid="service-sla-input"
              />
              <p className="text-[11px] text-slate-400">
                Operators see a live countdown in the case panel. Auto-escalates at 75% (amber → red) and on breach. <strong>0 = no SLA timer.</strong>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1" data-testid="save-service-btn">
              <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ============= USERS PANEL =============
const EMPTY_USER = { email: '', name: '', role: 'operator', password: '' };

const UsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/users');
      setUsers(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visibleUsers = useMemo(
    () => (showInactive ? users : users.filter((u) => u.active !== false)),
    [users, showInactive]
  );
  const inactiveCount = useMemo(
    () => users.filter((u) => u.active === false).length,
    [users]
  );

  const openCreate = () => { setForm(EMPTY_USER); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.email || !form.name) {
      toast.error('Email and name are required');
      return;
    }
    setSaving(true);
    try {
      // Backend auto-generates a temp password and emails it to the new user.
      // (We strip the local password field; if you want admin-defined password support, send form.password instead.)
      const payload = { email: form.email, name: form.name, role: form.role };
      await api.post('/users', payload);
      toast.success(`Invitation email sent to ${form.email}`);
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? `${u.email} deactivated` : `${u.email} reactivated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resendInvite = async (u) => {
    if (!u.active) {
      toast.error('Re-activate the user before resending an invite.');
      return;
    }
    if (!window.confirm(
      `Resend invite to ${u.email}?\n\n`
      + 'A new temporary password will be generated and emailed to the user. '
      + 'Their existing password will stop working, and they will be forced '
      + 'to set a new one on next login.',
    )) return;
    try {
      const r = await api.post(`/users/${u.id}/resend-invite`);
      toast.success(`Invite resent to ${r.data.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const roleLabel = { admin: 'Administrator', operator: 'Control Room', accounts: 'Accounts' };

  return (
    <Card className="bg-white border-slate-200" data-testid="users-panel">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading text-lg text-[#0D5B69]">System Users</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Manage who can log into the CSG platform and what they can do.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="show-inactive-users-toggle">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-[#80C030]"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
            {inactiveCount > 0 && (
              <span className="text-[10px] text-slate-400">({inactiveCount})</span>
            )}
          </label>
          <Button
            className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold"
            onClick={openCreate}
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4" /> Add User
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>
            ) : visibleUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-slate-400" data-testid="empty-users">
                  {users.length === 0
                    ? 'No users yet.'
                    : `No active users. ${inactiveCount} inactive — toggle "Show inactive" to view.`}
                </TableCell>
              </TableRow>
            ) : visibleUsers.map((u, idx) => (
              <TableRow key={u.id} className="hover:bg-slate-50/80" data-testid={`user-row-${idx}`}>
                <TableCell className="text-sm text-slate-900 font-medium">{u.email}</TableCell>
                <TableCell className="text-sm text-slate-700">{u.name}</TableCell>
                <TableCell>
                  {u.role === 'customer' ? (
                    <Badge
                      className="bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-50"
                      title="Portal user — scoped to one customer. Manage from Customers → Portal Users."
                      data-testid={`role-portal-${idx}`}
                    >
                      Customer Portal
                    </Badge>
                  ) : (
                    <Select value={u.role} onValueChange={(v) => updateRole(u, v)}>
                      <SelectTrigger className="h-8 w-40" data-testid={`role-select-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">{roleLabel.admin}</SelectItem>
                        <SelectItem value="operator">{roleLabel.operator}</SelectItem>
                        <SelectItem value="accounts">{roleLabel.accounts}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  {u.active ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">Active</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => resendInvite(u)}
                      disabled={!u.active}
                      title={u.active
                        ? `Resend invite email to ${u.email}`
                        : 'Re-activate the user before resending an invite'}
                      data-testid={`resend-invite-${idx}`}
                    >
                      <Mail className={`w-4 h-4 ${u.active ? 'text-[#0D5B69]' : 'text-slate-300'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleActive(u)} data-testid={`toggle-user-${idx}`}>
                      <Power className={`w-4 h-4 ${u.active ? 'text-amber-600' : 'text-green-600'}`} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0D5B69]">New User</DialogTitle>
            <DialogDescription>
              An email with a temporary password will be sent to the user. They'll be required to set
              their own password on first sign-in, then verify with an OTP.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@csgconnect.co.za" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Full Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="operator">Control Room Operator</SelectItem>
                  <SelectItem value="accounts">Accounts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-md bg-[#80C030]/10 border border-[#80C030]/30 p-3">
              <p className="text-xs text-[#0D5B69] font-semibold mb-1">📧 Welcome email</p>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                The system will generate a secure temporary password and email it to this user along with
                a sign-in link. They will be forced to choose a new password on first login.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1" data-testid="save-user-btn">
              <Save className="w-4 h-4" /> {saving ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};


// ============= NOTE TEMPLATES PANEL =============
const EMPTY_TEMPLATE = { label: '', text: '', icon: '', sort_order: 0 };

const NoteTemplatesPanel = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_TEMPLATE);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/note-templates', { params: { include_inactive: true } });
      setTemplates(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_TEMPLATE, sort_order: (templates.length + 1) * 10 });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ label: t.label, text: t.text, icon: t.icon || '', sort_order: t.sort_order || 0 });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.label.trim() || !form.text.trim()) {
      toast.error('Label and phrase text are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, icon: form.icon || null };
      if (editingId) {
        await api.patch(`/note-templates/${editingId}`, payload);
        toast.success(`Template "${form.label}" updated`);
      } else {
        await api.post('/note-templates', payload);
        toast.success(`Template "${form.label}" created`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/note-templates/${t.id}`, { active: !t.active });
      toast.success(t.active ? `"${t.label}" hidden` : `"${t.label}" reactivated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Remove "${t.label}" from the operator quick-tap chips?`)) return;
    try {
      await api.delete(`/note-templates/${t.id}`);
      toast.success(`"${t.label}" removed`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="bg-white border-slate-200" data-testid="note-templates-panel">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading text-lg text-[#0D5B69]">Operator Quick-Tap Phrases</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            One-tap chips shown above the Note textarea inside any case panel. Click a chip → the phrase is appended to the draft note.
          </p>
        </div>
        <Button
          className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold"
          onClick={openCreate}
          data-testid="add-template-btn"
        >
          <Plus className="w-4 h-4" /> Add Phrase
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">Order</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Chip label</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Inserted text</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>
            ) : templates.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400" data-testid="empty-templates">No phrases yet. Click <strong>Add Phrase</strong> to create your first quick-tap chip.</TableCell></TableRow>
            ) : templates.map((t, idx) => (
              <TableRow key={t.id} className={`hover:bg-slate-50/80 ${t.active ? '' : 'opacity-60'}`} data-testid={`template-row-${idx}`}>
                <TableCell className="text-xs text-slate-500 font-mono">
                  <span className="inline-flex items-center gap-1"><GripVertical className="w-3 h-3 text-slate-300" />{t.sort_order}</span>
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#0D5B69]/20 bg-white text-[12px] font-medium text-[#0D5B69]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#80C030]" />
                    {t.label}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-slate-600 max-w-[420px] truncate italic">{t.text}</TableCell>
                <TableCell>
                  {t.active ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">Active</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100">Hidden</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(t)} data-testid={`edit-template-${idx}`} title="Edit">
                      <Edit className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleActive(t)} data-testid={`toggle-template-${idx}`} title={t.active ? 'Hide' : 'Reactivate'}>
                      <Power className={`w-4 h-4 ${t.active ? 'text-amber-600' : 'text-green-600'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(t)} data-testid={`delete-template-${idx}`} title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0D5B69]">{editingId ? 'Edit Phrase' : 'New Quick-Tap Phrase'}</DialogTitle>
            <DialogDescription>
              The <strong>chip label</strong> appears on the button; the <strong>inserted text</strong> is what gets appended to the operator's note.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Chip label *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Vehicle recovered"
                maxLength={32}
                data-testid="template-label-input"
              />
              <p className="text-[10px] text-slate-400">Keep it short — 1–3 words fits best on the chip.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Inserted text *</Label>
              <Input
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="e.g. Vehicle recovered — "
                data-testid="template-text-input"
              />
              <p className="text-[10px] text-slate-400">Tip: end with a space or dash so the operator can keep typing inline.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Sort order</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                data-testid="template-sort-input"
              />
              <p className="text-[10px] text-slate-400">Lower number = shows first. Step by 10 to leave room for inserts.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1" data-testid="save-template-btn">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create Phrase')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};



// ============= VEHICLE CATALOGUE PANEL =============
const KIND_LABELS = { make: 'Makes', colour: 'Colours', type: 'Types' };

const VehicleCataloguePanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ kind: 'make', label: '', sort_order: 10 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/vehicle-catalogue', { params: { include_inactive: true } });
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const out = { make: [], colour: [], type: [] };
    for (const it of items) (out[it.kind] || []).push(it);
    return out;
  }, [items]);

  const openCreate = (kind) => {
    setEditingId(null);
    setForm({ kind, label: '', sort_order: 10 });
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({ kind: it.kind, label: it.label, sort_order: it.sort_order });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) {
      toast.error('Label is required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/vehicle-catalogue/${editingId}`, {
          label: form.label.trim(), sort_order: Number(form.sort_order),
        });
        toast.success('Updated');
      } else {
        await api.post('/vehicle-catalogue', {
          kind: form.kind, label: form.label.trim(), sort_order: Number(form.sort_order),
        });
        toast.success(`${KIND_LABELS[form.kind].slice(0, -1)} added`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.patch(`/vehicle-catalogue/${it.id}`, { active: !it.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Delete "${it.label}" from ${KIND_LABELS[it.kind]}?`)) return;
    try {
      await api.delete(`/vehicle-catalogue/${it.id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const renderSection = (kind) => (
    <Card className="border-slate-200" key={kind}>
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-base text-[#0D5B69]">{KIND_LABELS[kind]}</CardTitle>
        <Button size="sm" className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1" onClick={() => openCreate(kind)} data-testid={`add-vehicle-${kind}-btn`}>
          <Plus className="w-3.5 h-3.5" /> Add
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <p className="px-4 py-6 text-sm text-slate-400 text-center">Loading…</p>
        ) : grouped[kind].length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400 text-center">No entries yet. Click <strong>Add</strong> to create the first one.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Label</TableHead>
                <TableHead className="w-20 text-center">Order</TableHead>
                <TableHead className="w-20 text-center">Status</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grouped[kind].map((it) => (
                <TableRow key={it.id} className={it.active ? '' : 'opacity-50'} data-testid={`vehicle-${kind}-${it.id}`}>
                  <TableCell><GripVertical className="w-3.5 h-3.5 text-slate-300" /></TableCell>
                  <TableCell className="font-medium">{it.label}</TableCell>
                  <TableCell className="text-center text-xs text-slate-500">{it.sort_order}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={it.active ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100' : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100'}>
                      {it.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(it)} title="Edit"><Edit className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => toggleActive(it)} title={it.active ? 'Deactivate' : 'Activate'}><Power className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(it)} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="vehicle-catalogue-panel">
      <div className="rounded-md bg-[#0D5B69]/[0.04] border border-[#0D5B69]/15 p-3">
        <p className="text-xs text-[#0D5B69] leading-snug">
          <strong>Vehicle Catalogue</strong> — these three lists power the dropdowns in the New Incident dialog (Vehicle of Interest section).
          Add the makes, colours, and types CSG works with most often. Lower sort-order values appear first in each dropdown.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {['make', 'colour', 'type'].map(renderSection)}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0D5B69]">
              {editingId ? `Edit ${KIND_LABELS[form.kind].slice(0, -1)}` : `Add ${KIND_LABELS[form.kind].slice(0, -1)}`}
            </DialogTitle>
            <DialogDescription>
              Appears in the {KIND_LABELS[form.kind]} dropdown on the New Incident dialog.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Label *</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder={form.kind === 'make' ? 'e.g. Toyota' : form.kind === 'colour' ? 'e.g. Pearl white' : 'e.g. Hatchback'}
                data-testid="vehicle-catalogue-label-input" autoFocus />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Sort order</Label>
              <Input type="number" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                data-testid="vehicle-catalogue-sort-input" />
              <p className="text-[10px] text-slate-400">Lower numbers appear first. Leave 10/20/30 for room to insert later.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1" data-testid="save-vehicle-catalogue-btn">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
